import {
  CheckCircle,
  CheckCircleOutline,
  Edit,
  HighlightOff,
  MarkChatUnread,
  PersonAddAlt,
  PhotoLibrary,
} from '@mui/icons-material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  useMediaQuery,
} from '@mui/material';
import get from 'lodash.get';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OrderTypes, ProcessStep } from '../../../common/enums/enums';
import theme from '../../../styles/ThemeConfig';
import GenDataText from '../../GenDataText';
import GenSkeleton from '../../GenSkeleton';
import GenTables from '../../GenTables';
import PhotoGalleryPopUp from '../../PhotoGalleryPopUp';
import SectionTitle from '../../SectionTitle';
import GlobalColumns from './GlobalColumns';

const cardValuesHandler = (obj, data) => {
  const values = data.dataSource.map(i => {
    if (Array.isArray(i)) return i.map(item => get(obj, item));
    if (typeof get(obj, i) === 'number') return get(obj, i);
    if (!get(obj, i) && data.defaultValue) return data.defaultValue;
    if (!get(obj, i)) return i;
    return get(obj, i);
  });
  return Array.isArray(values[0]) ? values[0] : values.join('');
};

const rowCardDeclinedStyle = data => {
  if (data.declinedAt) return { backgroundColor: theme.palette.grey[100] };
  return null;
};

const ReconServicesStatus = ({ text: value, markAsDone, isDealer }) => {
  const { t } = useTranslation('workOrdersTable');
  const declineAt = value[0];
  const completedAt = value[1];
  const serviceId = value[2];

  const statusHandler = (decAt, compAt, dealer) => {
    if (!decAt && !compAt && dealer)
      return (
        <GenDataText
          direction="vertical"
          alignItems="flex-start"
          text={'-'}
          textColor="textSecondary"
          textVariant="body2"
        />
      );
    if (!decAt && !compAt)
      return (
        <Button
          value={serviceId}
          variant="text"
          startIcon={<CheckCircleOutline />}
          color="primary"
          onClick={!isDealer && markAsDone}
          disabled={isDealer}
          sx={
            isDealer
              ? {
                  '&.Mui-disabled': {
                    color: theme.palette.primary.main,
                  },
                }
              : {}
          }
        >
          {t('detail.reconServices.statusButtons.markAsDone')}
        </Button>
      );
    if (decAt)
      return (
        <Button
          variant="text"
          startIcon={<HighlightOff />}
          color="error"
          onClick={e => e.preventDefault}
          disabled={true}
          sx={{
            '&.Mui-disabled': {
              color: theme.palette.error.main,
            },
          }}
        >
          {t('detail.reconServices.statusButtons.declined')}
        </Button>
      );
    if (compAt)
      return (
        <Button
          variant="text"
          startIcon={<CheckCircle />}
          color="success"
          onClick={e => e.preventDefault}
          disabled={true}
          sx={{
            '&.Mui-disabled': {
              color: theme.palette.success.main,
            },
          }}
        >
          {t('detail.reconServices.statusButtons.done')}
        </Button>
      );
    return null;
  };

  return statusHandler(declineAt, completedAt, isDealer);
};

export const PhotosAndNotesButtons = ({ text: values, type, onPress }) => {
  const photos = values[0];
  const notes = values[1];
  const [photoNotesState, setPhotoNotesState] = useState(false);
  const handleModalClose = () => {
    setPhotoNotesState(false);
  };

  const buttonsHandler = () => {
    if (photos && photos.length && type === 'photos')
      return (
        <IconButton
          color="primary"
          size="medium"
          onClick={() => setPhotoNotesState(true)}
        >
          <PhotoLibrary />
        </IconButton>
      );
    if (notes && type === 'notes')
      return (
        <IconButton
          color="primary"
          size="medium"
          onClick={() => setPhotoNotesState(true)}
        >
          <MarkChatUnread size="medium" />
        </IconButton>
      );
    return null;
  };

  return (
    <>
      {buttonsHandler()}
      <PhotoGalleryPopUp
        open={photoNotesState}
        images={photos}
        initialSlide={1}
        handleModalState={handleModalClose}
        isServicesModal={true}
        notes={notes}
      />
    </>
  );
};

const AddPhotosHandler = ({ text: values, onPress, step, orderType }) => {
  const vendorOrderServiceId = values[0];
  const servicePriceId = values[1];
  const price = values[2];
  const quantity = values[3];

  if (step !== 'Generated' || orderType === 'work_order') return null;
  return (
    <IconButton
      color="primary"
      size="medium"
      data-testid={'add-photo-press'}
      onClick={() =>
        onPress({ servicePriceId, vendorOrderServiceId, price, quantity })
      }
    >
      <AddPhotoAlternateIcon />
    </IconButton>
  );
};

export const AssigneeHandler = ({
  text: values,
  isCard = false,
  columns,
  assignTech,
  isDealer,
  isVendorManager = false,
  orderType = OrderTypes.RECONDITIONING,
  step,
}) => {
  const { t } = useTranslation('workOrdersTable');

  const [
    declined,
    assignedUser,
    serviceId,
    vendorOrderServiceId,
    assignedTechnicians,
  ] = values;

  const isDeclined = Boolean(declined);
  const isAssigned = Boolean(assignedUser);
  const hasAssignedTechnicians =
    Array.isArray(assignedTechnicians) && assignedTechnicians.length > 0;

  const disableAssignBtn =
    !isVendorManager &&
    step === ProcessStep.DONE &&
    orderType === OrderTypes.WORK_ORDER;

  const canAssign =
    !isDealer && (orderType !== OrderTypes.WORK_ORDER || isVendorManager);

  const canEdit =
    !isDealer && (orderType !== OrderTypes.WORK_ORDER || isVendorManager);

  const handleAssign = () => {
    assignTech(serviceId, vendorOrderServiceId);
  };

  const getAssigneeText = assignee => {
    if (Array.isArray(assignee)) {
      return assignee.map(a => `${a.firstName} ${a.lastName}`).join(', ');
    }

    if (assignee) {
      return `${assignee.firstName} ${assignee.lastName}`;
    }

    return '-';
  };

  const renderAssignButton = disabled => (
    <Button
      variant="text"
      startIcon={<PersonAddAlt />}
      color="primary"
      onClick={handleAssign}
      disabled={disabled}
    >
      {t('detail.reconServices.assigneeBtn')}
    </Button>
  );

  const renderEmpty = () => (
    <GenDataText
      direction="vertical"
      text="-"
      alignItems="flex-start"
      textVariant="caption"
      textColor="textPrimary"
    />
  );

  const renderAssignedRow = text => (
    <Stack
      direction="row"
      flexWrap="nowrap"
      alignItems="center"
      justifyContent="space-between"
    >
      <GenDataText
        textVariant="body2"
        textColor={isDeclined ? 'textSecondary' : 'textPrimary'}
        text={text}
      />
      {canEdit && (
        <IconButton size="small" disabled={isDeclined} onClick={handleAssign}>
          <Edit fontSize="inherit" />
        </IconButton>
      )}
    </Stack>
  );

  if (!isAssigned && !hasAssignedTechnicians) {
    if (isCard) {
      return (
        <Stack direction="column">
          <GenDataText
            direction="vertical"
            text={columns[4].label}
            alignItems="flex-start"
            textVariant="caption"
            textColor="textPrimary"
          />
          {canAssign ? renderAssignButton(isDeclined) : renderEmpty()}
        </Stack>
      );
    }

    return canAssign
      ? renderAssignButton(isDeclined || disableAssignBtn)
      : renderEmpty();
  }

  const assigneeText = getAssigneeText(
    hasAssignedTechnicians ? assignedTechnicians : assignedUser
  );

  if (isCard) {
    return (
      <Stack
        direction="row"
        alignItems="flex-end"
        justifyContent="space-between"
        width="100%"
      >
        <GenDataText
          direction="vertical"
          label={columns[4].label}
          alignItems="flex-start"
          labelVariant="caption"
          text={assigneeText}
          textVariant="body2"
          textColor="textSecondary"
        />
        {canEdit && (
          <IconButton size="small" disabled={isDeclined} onClick={handleAssign}>
            <Edit fontSize="inherit" />
          </IconButton>
        )}
      </Stack>
    );
  }

  return renderAssignedRow(assigneeText);
};

const Columns = (
  t,
  markAsDone,
  isDealer,
  assignTech,
  onPress,
  step,
  orderType
) => {
  return [
    {
      id: 'name',
      label: t('detail.servicesTable.servicesColumnLabels.nameLabel'),
      labelComponent: (
        <GenDataText textVariant="body1" textColor="textPrimary" />
      ),
      component: <GenDataText textVariant="body2" textColor="textPrimary" />,
      dataSource: ['quantity', 'x ', 'name'],
      align: 'inherit',
    },
    ...GlobalColumns(t),
    {
      id: 'assignee',
      label: t('detail.reconServices.columns.assignTo'),
      labelComponent: (
        <GenDataText textVariant="body1" textColor="textPrimary" />
      ),
      component: (
        <AssigneeHandler assignTech={assignTech} isDealer={isDealer} />
      ),
      dataSource: [
        [
          'declinedAt',
          'assignee',
          'serviceId',
          'vendorOrderServiceId',
          'assignedTechnicians',
        ],
      ],
      defaultValue: ' ',
      align: 'left',
      sx: { width: '185px' },
    },
    {
      id: 'status',
      label: t('detail.reconServices.columns.status'),
      labelComponent: (
        <GenDataText textVariant="body1" textColor="textPrimary" />
      ),
      component: (
        <ReconServicesStatus markAsDone={markAsDone} isDealer={isDealer} />
      ),
      dataSource: [['declinedAt', 'completedAt', 'serviceId']],
      defaultValue: ' ',
      align: 'left',
      sx: { width: '185px' },
    },
    {
      id: 'addPhotos',
      label: '',
      labelComponent: '',
      component: (
        <AddPhotosHandler onPress={onPress} step={step} orderType={orderType} />
      ),
      dataSource: [
        ['vendorOrderServiceId', 'servicePriceId', 'price', 'quantity'],
      ],
      sx: { width: '10px', px: 0 },
    },
    {
      id: 'photos',
      label: '',
      labelComponent: '',
      component: <PhotosAndNotesButtons type={'photos'} />,
      dataSource: [['attachments', 'notes']],
      sx: { width: '10px', px: 0 },
    },
    {
      id: 'notes',
      label: '',
      labelComponent: '',
      component: <PhotosAndNotesButtons type={'notes'} />,
      dataSource: [['attachments', 'notes']],
      sx: { width: '10px', pl: 0 },
    },
  ];
};
const Cards = ({ data, columns, markAsDone, isDealer, assignTech }) => {
  const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));
  return data.map((d, i) => {
    return (
      <Card
        sx={{
          marginBottom: 2,
        }}
        key={i}
      >
        <CardContent
          sx={{
            padding: '0px',
            '&:last-child': { padding: '0px' },
          }}
        >
          <Box sx={{ backgroundColor: theme.palette.grey[50], p: 2 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <GenDataText
                labelVariant="subtitle2"
                labelColor="textPrimary"
                label={cardValuesHandler(d, columns[0])}
                text={' '}
              />
              <GenDataText
                textVariant="subtitle2"
                textColor="textPrimary"
                text={cardValuesHandler(d, columns[1])}
                currency={true}
              />
            </Stack>
          </Box>
          <Box
            sx={{
              padding: 2,
              width: '100%',
              ...rowCardDeclinedStyle({
                declinedAt: cardValuesHandler(d, columns[5])[0],
              }),
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="start"
              flexWrap="wrap"
              gap={1}
            >
              <Stack
                direction="column"
                alignItems="flex-start"
                gap={1}
                sx={{ minWidth: '140px', maxWidth: '45%' }}
              >
                <GenDataText
                  direction="vertical"
                  label={columns[2].label}
                  alignItems="flex-start"
                  labelVariant="caption"
                  text={cardValuesHandler(d, columns[2])}
                  textColor="textSecondary"
                  textVariant="body2"
                />
                <AssigneeHandler
                  text={cardValuesHandler(d, columns[4])}
                  isCard={true}
                  columns={columns}
                  d={d}
                  assignTech={assignTech}
                  isDealer={isDealer}
                />
              </Stack>
              <Stack
                direction="column"
                alignItems="flex-start"
                gap={1}
                sx={{ minWidth: '152px' }}
              >
                <GenDataText
                  direction="vertical"
                  label={columns[3].label}
                  alignItems="flex-start"
                  labelVariant="caption"
                  text={cardValuesHandler(d, columns[3])}
                  textColor="textSecondary"
                  textVariant="body2"
                />
                <Stack direction="column" alignItems="flex-start">
                  <GenDataText
                    direction="vertical"
                    text={columns[5].label}
                    alignItems="flex-start"
                    textColor="textPrimary"
                    textVariant="caption"
                  />
                  <ReconServicesStatus
                    markAsDone={markAsDone}
                    text={cardValuesHandler(d, columns[5])}
                    isDealer={isDealer}
                  />
                </Stack>
              </Stack>
              <Stack
                direction={isExtraSmall ? 'row' : 'column'}
                justifyContent="flex-end"
                alignSelf="flex-end"
                sx={{
                  width: isExtraSmall ? '100%' : 'auto',
                }}
              >
                <PhotosAndNotesButtons
                  type={'photos'}
                  text={cardValuesHandler(d, columns[7])}
                />
                <PhotosAndNotesButtons
                  type={'notes'}
                  text={cardValuesHandler(d, columns[7])}
                />
              </Stack>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    );
  });
};

const WOrderReconServices = ({
  data,
  isLoading,
  markAsDone,
  isDealer,
  assignTech,
  onPressAddPhoto,
}) => {
  const { t } = useTranslation('workOrdersTable');
  const columns = Columns(
    t,
    markAsDone,
    isDealer,
    assignTech,
    onPressAddPhoto,
    data?.step,
    data?.orderType
  );

  return !isLoading ? (
    <Box
      sx={{
        bgcolor: 'background.paper',
        p: { xs: 2, lg: 4 },
        borderTop: '1px solid',
        borderColor: theme.palette.divider,
      }}
      data-testid="recon-services"
    >
      <Grid container flexDirection="column" spacing={0.5}>
        <Grid item>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <SectionTitle
                title={t('detail.servicesTable.services')}
                subsection={true}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item sx={{ display: { xs: 'none', md: 'block' } }}>
          <GenTables
            tableHeadSx={{ backgroundColor: 'grey.50' }}
            data={data.services}
            columns={columns}
            rowSXRules={rowCardDeclinedStyle}
          />
        </Grid>
        <Grid item sx={{ display: { xs: 'block', md: 'none' } }}>
          <Cards
            data={data.services}
            columns={columns}
            markAsDone={markAsDone}
            isDealer={isDealer}
            assignTech={assignTech}
            onPressAddPhoto={onPressAddPhoto}
          />
        </Grid>
      </Grid>
    </Box>
  ) : (
    <Box
      data-testid="skeleton"
      sx={{
        bgcolor: 'background.paper',
        p: { xs: 2, lg: 4 },
        borderTop: '1px solid',
        borderColor: theme.palette.divider,
      }}
    >
      <Grid container flexDirection="column" spacing={2}>
        <Grid item>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <Skeleton variant="text" width={120} />
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <GenSkeleton sx={{ height: '290px' }} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default WOrderReconServices;
